import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { PLAN_NAMES, day, useDebounced, useLoad, when } from "@/lib/staff";
import { BTN_GHOST, INPUT, PageHead, Pager, RoleBadge, Select, StatusBadge, Table } from "@/components/staff/ui";

const PLANS = [["", "All plans"], ["free", "Free"], ["starter", "Starter"], ["plus", "Plus"], ["unlimited", "Unlimited"]];
const STATUSES = [["", "Any status"], ["active", "Active"], ["suspended", "Suspended"], ["deletion", "Deletion scheduled"]];
const ACTIVE = [["", "Any activity"], ["today", "Active today"], ["7d", "Active in 7 days"], ["30d", "Active in 30 days"], ["inactive30", "Not active in 30 days"]];

const COLUMNS = [
  { key: "name", label: "Student", render: (r) => <div className="min-w-0"><p className="truncate text-neutral-100">{r.name || "—"}</p><p className="truncate text-xs text-neutral-500">{r.email}</p></div> },
  { key: "plan", label: "Plan", render: (r) => PLAN_NAMES[r.plan] || r.plan },
  { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  { key: "staff_role", label: "Staff", render: (r) => <RoleBadge role={r.staff_role} /> },
  { key: "created_at", label: "Signed up", right: true, render: (r) => day(r.created_at) },
  { key: "last_seen_at", label: "Last active", right: true, render: (r) => when(r.last_seen_at) },
];

function useFilters() {
  const [sp, setSp] = useSearchParams();
  const set = (k, v) => setSp((prev) => {
    const n = new URLSearchParams(prev);
    if (v) n.set(k, v); else n.delete(k);
    if (k !== "page") n.delete("page");
    return n;
  }, { replace: true });
  return { sp, set, clear: () => setSp(new URLSearchParams(), { replace: true }) };
}

function SearchBox({ q, onSearch }) {
  const [text, setText] = useState(q);
  const debounced = useDebounced(text);
  const latest = useRef({ q, onSearch });
  latest.current = { q, onSearch };
  useEffect(() => { setText(q); }, [q]);
  // Only typing searches; a new search from the top bar just refills the box.
  useEffect(() => {
    const { q: current, onSearch: go } = latest.current;
    if (debounced.trim() !== current) go(debounced.trim());
  }, [debounced]);
  return <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search name or email" className={`${INPUT} w-64`} aria-label="Search students" data-testid="students-search-input" />;
}

export default function StudentsPage() {
  const navigate = useNavigate();
  const { sp, set, clear } = useFilters();
  const key = sp.toString();
  const params = useMemo(() => Object.fromEntries(new URLSearchParams(key)), [key]);
  const { data, error, loading, reload } = useLoad("/staff/students", params);
  const page = Number(params.page || 1);
  return (
    <div data-testid="staff-students">
      <PageHead title="Students" sub="Find an account to see its plan, devices and status. Opening a record is saved in the audit log." />
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="students-filters">
        <SearchBox q={params.q || ""} onSearch={(v) => set("q", v)} />
        <Select value={params.plan || ""} onChange={(v) => set("plan", v)} options={PLANS} label="Plan" testid="students-plan-filter" />
        <Select value={params.status || ""} onChange={(v) => set("status", v)} options={STATUSES} label="Account status" testid="students-status-filter" />
        <Select value={params.active || ""} onChange={(v) => set("active", v)} options={ACTIVE} label="Last activity" testid="students-active-filter" />
        <span className="text-xs text-neutral-500 ml-1">Signed up</span>
        <input type="date" value={params.signup_from || ""} onChange={(e) => set("signup_from", e.target.value)} className={INPUT} aria-label="Signed up from" data-testid="students-signup-from" />
        <input type="date" value={params.signup_to || ""} onChange={(e) => set("signup_to", e.target.value)} className={INPUT} aria-label="Signed up to" data-testid="students-signup-to" />
        {key && <button onClick={clear} className={BTN_GHOST} data-testid="students-clear-filters"><X className="h-3.5 w-3.5" />Clear</button>}
      </div>
      <Table testid="students-table" columns={COLUMNS} rows={data?.items} rowKey={(r) => r.user_id} loading={loading} error={error} onRetry={reload}
        empty="No students match these filters." onRowClick={(r) => navigate(`/staff/students/${r.user_id}`)} />
      {data && <Pager page={page} pageSize={data.page_size} total={data.total} onPage={(p) => set("page", String(p))} testid="students-pager" />}
    </div>
  );
}
